// useTurnRouting.ts — what happens when the user sends, and how a turn's
// frames reach the transcript.
//
// handleSend routes what the user typed to whichever of three things is
// right for the session's current state (see sendRoute in chatState.ts).
// The composer itself is never disabled, so this has to have an answer
// for all of them:
//
//   a turn is running  → steer it. The message joins the turn instead of
//                        waiting for it.
//   the pod isn't up   → hold it and send when the pod is ready. Losing
//                        what they typed, or making them re-type it a
//                        minute later, would be the session's problem
//                        made the user's.
//   otherwise          → an ordinary new turn, via startTurn.
//
// startTurn has two ways for the reply to arrive, and the difference is
// who owns the turn:
//
//   over the session's socket, the ordinary path. POST /api/turns returns
//     as soon as the Pod has started the turn, and the turn belongs to the
//     Pod from then on. It survives a reload, and every watcher sees it.
//
//   over the response, when that session has no socket. The turn is the
//     response, so it only exists for as long as this request does. Kept
//     because a proxy that refuses WebSocket upgrades must degrade to a
//     working chat rather than a broken one. The fallback suppresses the
//     session's transcript frames while it runs: /api/chat publishes its
//     turn to the harness bus like any other, so a socket that reconnected
//     part-way through would replay into the bubble this response is
//     already filling.
//
// Also points the focused session's socket frames at the transcript
// writers (setFocus), and drains a held message once its pod is ready.
import React, { useEffect, useState } from 'react';
import { sessionsApi } from '../api';
import type { Session, SessionMessage } from '../api';
import { sendRoute } from './chatState';
import { activityOf, setFocus, suppressTranscript } from './live/sessionLive';
import type { LiveSnapshot, SessionActivity, SessionPhase } from './live/sessionLive';
import { appendSteer, appendUserMessage } from './transcript';
import { shouldDrainQueue, turnBusyId } from './turnState';
import type { QueuedMessage } from './turnState';
import type { TranscriptWriters } from './useTranscript';

/** How long a send may wait for turn_start before the composer gives up on it. */
const SUBMIT_TIMEOUT_MS = 15000;

export interface TurnRoutingArgs {
  selectedId: string | null;
  sessions: Session[];
  live: LiveSnapshot;
  selectedActivity: SessionActivity;
  /** The session whose frames may write to the transcript; '' for none. */
  focusedId: string;
  sessionPhase: SessionPhase | null;
  /** The composer's mirrored markdown (lags the editor; see handleSend). */
  input: string;
  setInput: (v: string) => void;
  setEditorEpoch: React.Dispatch<React.SetStateAction<number>>;
  setMessages: React.Dispatch<React.SetStateAction<SessionMessage[]>>;
  setStreamError: (msg: string) => void;
  writers: TranscriptWriters;
  /** Wakes a suspended workspace; a queued message drains on ready. */
  handleResume: () => Promise<void>;
}

export interface TurnRouting {
  /** A turn is in flight, as far as this tab can tell. */
  streaming: boolean;
  /** The session that turn belongs to. */
  streamingId: string | null;
  /** A message held for a pod that isn't up yet. */
  queued: QueuedMessage | null;
  /** Give a held message back to the composer. */
  cancelQueued: () => void;
  /** Send `live` (or the mirrored input) the way the session's state calls for. */
  handleSend: (live?: string) => Promise<void>;
}

export function useTurnRouting(a: TurnRoutingArgs): TurnRouting {
  const {
    selectedId, sessions, live, selectedActivity, focusedId, sessionPhase,
    input, setInput, setEditorEpoch, setMessages, setStreamError, writers, handleResume,
  } = a;
  const {
    upsertToolCall, upsertToolUi, appendToLastAssistant, appendTurnError, openTurnBubbles,
  } = writers;

  // Which session this tab is mid-send to, from the click until the
  // harness says the turn began. A second send landing in that window
  // would start a second turn, which the harness refuses with a 409.
  const [submitting, setSubmitting] = useState<string | null>(null);
  // Which session this tab is streaming a turn for over its own response,
  // rather than over the session's socket. Only the fallback sets this.
  const [sseStreamingId, setSseStreamingId] = useState<string | null>(null);
  // A message sent to a session whose pod isn't up yet. Carries the
  // session it was typed into — the user can switch away while it waits,
  // and delivering it elsewhere would put their message in someone else's
  // conversation.
  const [queued, setQueued] = useState<QueuedMessage | null>(null);

  const busyId = turnBusyId({
    sseStreamingId, submitting, selectedStatus: selectedActivity.status, selectedId,
  });
  const streaming = busyId !== null;
  const streamingId = busyId;

  // Point the focused session's frames at the writers. Re-run on every
  // change because the handlers close over component state; the sockets
  // themselves are reconciled separately and are untouched by this.
  useEffect(() => {
    setFocus(focusedId, {
      onState: (state) => {
        // Attaching to a session with a turn already in flight: open the
        // bubbles now so the deltas that follow have somewhere to go.
        const turn = state.active_turn as { prompt?: string } | undefined;
        if (state.active_turn_id) openTurnBubbles(String(turn?.prompt ?? ''));
      },
      onTurnStart: (_turnId, prompt) => {
        setSubmitting(null);
        openTurnBubbles(prompt);
      },
      onSteer: (message) => setMessages((prev) => appendSteer(prev, message)),
      onDelta: (text) => appendToLastAssistant('content', text),
      onThinking: (text) => appendToLastAssistant('reasoning', text),
      onToolUi: upsertToolUi,
      onToolStarted: (id, name, toolInput) =>
        upsertToolCall(id, { tool_name: name, tool_input: toolInput, tool_running: true }),
      onToolCompleted: (id, output, isError) =>
        upsertToolCall(id, { tool_output: output, tool_error: isError, tool_running: false }),
      onError: appendTurnError,
      onTurnEnd: () => setSubmitting(null),
    });
  }, [
    focusedId, openTurnBubbles, appendToLastAssistant, appendTurnError,
    upsertToolUi, upsertToolCall, setMessages,
  ]);

  // startTurn sends `content` as a new turn. The composer's own state is
  // not touched here — handleSend owns clearing it, because a message can
  // also reach this after sitting in the queue, long after the editor was
  // emptied.
  const startTurn = async (session: Session, content: string) => {
    setStreamError('');
    setMessages((prev) => appendUserMessage(prev, content));
    setSubmitting(session.id);

    // The Pod is provisioned with LLM_MODEL and only learns about a switch
    // from the turn that carries one. Sent every turn, so a Pod whose env
    // predates the stored choice re-syncs on the next message.
    const model = session.model || undefined;

    if (activityOf(live, session.id).socket === 'open') {
      try {
        console.debug('[sessions] starting turn over the session socket', { session: session.id });
        await sessionsApi.startTurn(session, content, model);
        // turn_start opens the assistant bubble and the deltas fill it.
        // But a socket that dies before that frame must not leave the
        // composer believing a turn is running for the rest of the
        // session, which would route everything typed after it as a steer
        // for a turn that does not exist.
        window.setTimeout(
          () => setSubmitting((cur) => (cur === session.id ? null : cur)),
          SUBMIT_TIMEOUT_MS,
        );
        return;
      } catch (err) {
        // The socket said the Pod was there and the POST disagreed. Fall
        // through to the response path rather than dropping the message.
        console.warn('startTurn via the control plane failed, streaming instead:', err);
      }
    }

    console.debug('[sessions] streaming turn over the response', { session: session.id });
    setSubmitting(null);
    setSseStreamingId(session.id);
    suppressTranscript(session.id, true);
    openTurnBubbles(content);
    const finish = () => {
      setSseStreamingId(null);
      suppressTranscript(session.id, false);
    };
    const stream = (target: Session) =>
      sessionsApi.streamMessage(target, content, {
        onDelta:    (d) => appendToLastAssistant('content', d),
        onThinking: (d) => appendToLastAssistant('reasoning', d),
        onToolUi:   upsertToolUi,
        onToolStarted: (id, name, toolInput) =>
          upsertToolCall(id, { tool_name: name, tool_input: toolInput, tool_running: true }),
        onToolCompleted: (id, output, isError) =>
          upsertToolCall(id, { tool_output: output, tool_error: isError, tool_running: false }),
        onDone: finish,
        onError: (m) => {
          finish();
          appendTurnError(m);
        },
      },
      model,
      );
    try {
      await stream(session);
    } catch (err) {
      // A transport failure before any response: retry once on the same route.
      setStreamError(`chat stream failed, retrying once: ${err}`);
      try {
        await stream(session);
      } catch (retryErr) {
        // No frame will ever arrive to clear the in-flight state the way
        // onDone/onError would, so clear it here — or the spinner keeps
        // claiming work on a dead request and the next send is routed as
        // a steer for a turn that does not exist.
        finish();
        setStreamError(`chat unreachable: ${retryErr}`);
      }
    }
  };

  // `liveText` is the editor's markdown read at the instant of submission.
  // Prefer it over `input`: the mirrored state lags the editor by up to
  // 200ms (Milkdown debounces its change listener), so reading `input`
  // here silently dropped the tail of a message typed quickly.
  const handleSend = async (liveText?: string) => {
    const content = (liveText ?? input).trim();
    if (!content || !selectedId) return;
    const session = sessions.find((s) => s.id === selectedId);
    if (!session) return;
    // Clear the composer up front, whichever branch takes it: the message
    // is now represented somewhere else, and leaving it in the editor
    // invites sending it twice.
    setInput('');
    setEditorEpoch((e) => e + 1);

    const route = sendRoute({
      streaming, streamingId, selectedId: session.id, phase: sessionPhase,
    });
    console.debug('[sessions] send routed', { session: session.id, route, phase: sessionPhase });

    if (route === 'steer') {
      try {
        const landed = await sessionsApi.steer(session, content);
        if (landed) {
          // Show it immediately; the Pod appends it to the transcript
          // before the next round-trip, but that can be a tool call away.
          setMessages((prev) => appendUserMessage(prev, content));
          return;
        }
        // Nothing was running after all — the turn ended between the
        // keystroke and the request. Fall through and send it as a new
        // turn rather than dropping it.
      } catch (err) {
        // Put it back in the editor. The epoch bump is what reseeds
        // Milkdown — it owns its own document.
        setStreamError(`could not add that to the running turn: ${err}`);
        setInput(content);
        setEditorEpoch((e) => e + 1);
        return;
      }
    }

    // Re-derived rather than reusing `route`: the steer branch may have
    // fallen through, and from here the answer is whatever the phase says.
    if (sessionPhase !== 'ready') {
      setQueued({ sessionId: session.id, content });
      // A suspended session needs waking before anything can be sent to
      // it; resume flips the phase and the queue drains on ready.
      if (sessionPhase === 'suspended') void handleResume();
      return;
    }

    await startTurn(session, content);
  };

  // Send a held message once its pod is actually up.
  useEffect(() => {
    if (!shouldDrainQueue(queued, selectedId, sessionPhase, streaming) || !queued) return;
    const session = sessions.find((s) => s.id === queued.sessionId);
    if (!session) return;
    console.debug('[sessions] draining held message', { session: session.id });
    setQueued(null);
    void startTurn(session, queued.content);
    // startTurn is redefined every render; depending on it would re-fire
    // this the moment it changes. The guards above are what make it safe
    // to key on the queue and the phase alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queued, sessionPhase, streaming, selectedId, sessions]);

  const cancelQueued = () => {
    if (!queued) return;
    setInput(queued.content);
    setQueued(null);
    setEditorEpoch((e) => e + 1);
  };

  return { streaming, streamingId, queued, cancelQueued, handleSend };
}
